import React from 'react';
import styled from 'styled-components';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ContactSupportIcon from '@material-ui/icons/ContactSupport';
import ClientContainer from '../../ClientContainer';
import BannerStrip from '../../Common/Containers/BannerStrip';
import openInNewTab from '../../../../../utils/openInNewTab';
import { link1, link2, link3, link4, link5 } from '../Dashboard/Tutorial/links';

type VideoMeta = {
  number: string;
  title: string;
  description: string;
  url: string;
  tone: 'blue' | 'green' | 'purple' | 'amber' | 'teal';
};

const videos: VideoMeta[] = [
  {
    number: '01',
    title: 'Introduction',
    description: 'Get a quick tour of the client portal and learn what you can do here.',
    url: link1,
    tone: 'blue',
  },
  {
    number: '02',
    title: 'Access Your Permits',
    description: 'Find active permits, review statuses, and drill into project details.',
    url: link2,
    tone: 'green',
  },
  {
    number: '03',
    title: 'Schedule Inspections',
    description: 'Request inspections directly from a permit and track their progress.',
    url: link3,
    tone: 'amber',
  },
  {
    number: '04',
    title: 'Pay Invoices',
    description: 'Review outstanding fees, choose invoices, and pay them securely online.',
    url: link4,
    tone: 'purple',
  },
  {
    number: '05',
    title: 'Upload Documents',
    description: 'Submit plans, receive review comments, and respond to document requests.',
    url: link5,
    tone: 'teal',
  },
];

const tonePalette: Record<VideoMeta['tone'], { bg: string; fg: string; border: string }> = {
  blue: { bg: '#E9F2FF', fg: '#2563EB', border: '#CFE6F3' },
  green: { bg: '#DCFCE7', fg: '#16A34A', border: '#C5E7D2' },
  amber: { bg: '#FFF1D6', fg: '#B7800A', border: '#FFE7AC' },
  purple: { bg: '#F3E8FF', fg: '#9333EA', border: '#E9D5FF' },
  teal: { bg: '#CFFAF4', fg: '#0E9488', border: '#A7E8DF' },
};

const HelpPage = () => {
  const openVideo = (video: VideoMeta) => () => openInNewTab(video.url);

  return (
    <ClientContainer>
      <BannerStrip label="Help" />
      <Page>
        <IntroCard>
          <IntroIcon>
            <ContactSupportIcon />
          </IntroIcon>
          <IntroBody>
            <IntroTitle>Welcome to the Elite Permits client portal</IntroTitle>
            <IntroText>
              These quick video tutorials walk you through the most common tasks. Click a tile to open the video on Canva.
            </IntroText>
          </IntroBody>
        </IntroCard>

        <SectionHeading>
          <SectionEyebrow>Video tutorials</SectionEyebrow>
          <SectionTitle>Step-by-step walkthroughs</SectionTitle>
        </SectionHeading>

        <Grid>
          {videos.map(video => {
            const pal = tonePalette[video.tone];
            return (
              <Tile key={video.number}>
                <Thumb
                  type="button"
                  onClick={openVideo(video)}
                  title={`Open on Canva: ${video.title}`}
                  style={{ background: pal.bg, borderColor: pal.border }}
                >
                  <ThumbOverlay aria-hidden="true">
                    <VideoNumber style={{ color: pal.fg }}>{video.number}</VideoNumber>
                    <PlayIcon style={{ color: pal.fg }}>
                      <PlayCircleOutlineIcon />
                    </PlayIcon>
                  </ThumbOverlay>
                </Thumb>
                <TileBody>
                  <TileTitle>{video.title}</TileTitle>
                  <TileText>{video.description}</TileText>
                </TileBody>
                <WatchBtn type="button" onClick={openVideo(video)} aria-label={`Open on Canva: ${video.title}`}>
                  <OpenInNewIcon style={{ fontSize: 16 }} />
                  <span>Open on Canva</span>
                </WatchBtn>
              </Tile>
            );
          })}
        </Grid>

        <FooterCard>
          <FooterTitle>Still need help?</FooterTitle>
          <FooterText>
            Reach out to your Elite Permits contact or your assigned project manager — we're happy to walk you through any
            step.
          </FooterText>
        </FooterCard>
      </Page>
    </ClientContainer>
  );
};

export default HelpPage;

const Page = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 4px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;

  @media only screen and (max-width: 1366px) {
    gap: 10px;
    padding: 0 4px 32px;
    flex: none;
    height: auto;
    max-height: calc(100vh - 160px);
    min-height: 0;
  }

  @media only screen and (max-width: 540px) {
    max-height: calc(100vh - 200px);
  }
`;

const IntroCard = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px 24px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-xs);
  box-sizing: border-box;

  @media only screen and (max-width: 1366px) {
    gap: 12px;
    padding: 12px 14px;
    border-radius: 10px;
  }

  @media only screen and (max-width: 640px) {
    flex-direction: row;
    align-items: center;
    padding: 10px 12px;
  }
`;

const IntroIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: var(--primary-50);
  color: var(--primary-600);
  border: 1px solid var(--primary-100, #cfe6f3);
  flex-shrink: 0;

  svg {
    font-size: 28px;
  }

  @media only screen and (max-width: 1366px) {
    width: 36px;
    height: 36px;
    border-radius: 8px;

    svg {
      font-size: 20px;
    }
  }
`;

const IntroBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const IntroTitle = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.3;

  @media only screen and (max-width: 1366px) {
    font-size: 0.875rem;
    line-height: 1.25;
  }
`;

const IntroText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--neutral-600);
  line-height: 1.5;

  @media only screen and (max-width: 1366px) {
    display: none;
  }
`;

const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 2px 0;
`;

const SectionEyebrow = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;

  @media only screen and (max-width: 1366px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const ThumbOverlay = styled.div.attrs({ className: 'thumb-overlay' })`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.95;
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
`;

const VideoNumber = styled.div`
  position: absolute;
  top: 12px;
  left: 14px;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
`;

const PlayIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0.85;
  transition: opacity 0.15s ease, transform 0.15s ease;

  svg {
    font-size: 64px;
  }
`;

const Thumb = styled.button`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  border: 1px solid transparent;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  background: var(--neutral-100);
  transition: box-shadow 0.15s ease;

  @supports not (aspect-ratio: 16 / 9) {
    height: 128px;
  }

  &:hover .thumb-overlay {
    opacity: 1;
  }

  &:hover svg {
    opacity: 1;
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  @media only screen and (max-width: 1366px) {
    width: 44px;
    height: 44px;
    aspect-ratio: auto;
    flex-shrink: 0;
    border-radius: 8px;

    ${ThumbOverlay} {
      background: none;
      opacity: 1;
    }

    ${VideoNumber} {
      position: static;
      font-size: 0.9375rem;
      text-shadow: none;
    }

    ${PlayIcon} {
      display: none;
    }

    @supports not (aspect-ratio: 16 / 9) {
      height: 44px;
    }
  }
`;

const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const TileTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.3;

  @media only screen and (max-width: 1366px) {
    font-size: 0.875rem;
  }
`;

const TileText = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-600);
  line-height: 1.45;

  @media only screen and (max-width: 1366px) {
    display: none;
  }
`;

const WatchBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: inherit;
  font-weight: 600;
  font-size: 0.8125rem;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  @media only screen and (max-width: 1366px) {
    height: 32px;
    padding: 0;
    width: 32px;
    border-radius: 8px;
    flex-shrink: 0;

    span {
      display: none;
    }
  }
`;

const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-xs);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: var(--neutral-300);
    box-shadow: var(--shadow-sm, 0 2px 6px rgba(15, 23, 42, 0.08));
    transform: translateY(-1px);
  }

  @media only screen and (max-width: 1366px) {
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;

    &:hover {
      transform: none;
    }
  }
`;

const FooterCard = styled.div`
  padding: 18px 20px;

  @media only screen and (max-width: 1366px) {
    padding: 12px 14px;
  }
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FooterTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--mainText);
`;

const FooterText = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-600);
  line-height: 1.5;
`;

